import { EmptyState } from '@/components/primitives/EmptyState'
import type { ProxyRow } from '@/lib/scraper'
import { scraperApi } from '@/lib/scraper'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, Loader2, Network, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  BTN_PRIMARY,
  CARD,
  CHIP_OFF,
  CHIP_ON,
  INPUT,
  LABEL,
  MUTED,
  PILL,
  SKELETON,
  cls
} from './ui'

export function ProxiesPanel() {
  const queryClient = useQueryClient()
  const [source, setSource] = useState<'' | 'manual' | 'free'>('')
  const [url, setUrl] = useState('')
  const [priority, setPriority] = useState(0)
  const [adding, setAdding] = useState(false)
  const proxies = useQuery({
    queryKey: ['scraper-proxies', source],
    queryFn: () => scraperApi.proxies(source)
  })
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['scraper-proxies'] })

  const add = async (event: React.FormEvent) => {
    event.preventDefault()
    setAdding(true)
    try {
      await scraperApi.createProxy({ url, priority })
      setUrl('')
      toast.success('Proxy saved (the password is stored encrypted)')
      await refresh()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not save the proxy'
      )
    } finally {
      setAdding(false)
    }
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={add}
        className={cls(
          CARD,
          'grid gap-3 md:grid-cols-[1fr_120px_auto] md:items-end'
        )}
      >
        <div>
          <label htmlFor="proxy-url" className={LABEL}>
            Proxy URL
          </label>
          <input
            id="proxy-url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="http://user:password@host:port"
            autoComplete="off"
            className={INPUT}
          />
        </div>
        <div>
          <label htmlFor="proxy-priority" className={LABEL}>
            Priority
          </label>
          <input
            id="proxy-priority"
            type="number"
            value={priority}
            onChange={(e) => setPriority(Number(e.target.value))}
            className={INPUT}
          />
        </div>
        <button type="submit" disabled={adding} className={BTN_PRIMARY}>
          {adding ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-4" />
          )}{' '}
          Add proxy
        </button>
        <p className={cls(MUTED, 'md:col-span-3')}>
          Proxies are only used after a site blocks the server. Your proxies
          always come before the free pool, and free proxies are only used for
          https sites.
        </p>
      </form>
      <div className="flex gap-2">
        {(['', 'manual', 'free'] as const).map((s) => (
          <button
            key={s || 'all'}
            type="button"
            aria-pressed={source === s}
            onClick={() => setSource(s)}
            className={source === s ? CHIP_ON : CHIP_OFF}
          >
            {s || 'all'}
          </button>
        ))}
      </div>
      {proxies.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={cls(SKELETON, 'h-16')} />
          ))}
        </div>
      ) : proxies.isError ? (
        <p className="text-sm text-red-500">Could not load proxies.</p>
      ) : !proxies.data?.results.length ? (
        <EmptyState
          icon={Network}
          title="No proxies"
          description="Add your own above, or turn on the free pool in Settings."
          className="rounded-[36px] border-neutral-200 dark:border-neutral-800"
        />
      ) : (
        <ul className="space-y-2">
          {proxies.data.results.map((p) => (
            <ProxyItem key={p.id} proxy={p} onChanged={refresh} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ProxyItem({
  proxy,
  onChanged
}: { proxy: ProxyRow; onChanged: () => void }) {
  const [busy, setBusy] = useState<'check' | 'toggle' | 'delete' | null>(null)
  const run = async (kind: 'check' | 'toggle' | 'delete') => {
    setBusy(kind)
    try {
      if (kind === 'check') {
        const r = await scraperApi.checkProxy(proxy.id)
        r.is_alive
          ? toast.success(`Alive, ${r.latency_ms} ms`)
          : toast.error('Proxy did not answer')
      } else if (kind === 'toggle') {
        await scraperApi.updateProxy(proxy.id, { enabled: !proxy.enabled })
      } else {
        await scraperApi.deleteProxy(proxy.id)
      }
      onChanged()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setBusy(null)
    }
  }
  return (
    <li className={cls(CARD, 'flex flex-wrap items-center gap-3 py-4')}>
      <span
        className={cls(
          'size-2.5 rounded-full',
          proxy.is_alive ? 'bg-emerald-500' : 'bg-red-500'
        )}
        aria-label={proxy.is_alive ? 'alive' : 'dead'}
      />
      <span className="min-w-0 flex-1 truncate font-mono text-sm text-neutral-900 dark:text-white">
        {proxy.host_display}
      </span>
      <span className={PILL}>{proxy.source}</span>
      <span className={MUTED}>
        {proxy.latency_ms != null ? `${proxy.latency_ms} ms · ` : ''}
        {proxy.success_count} ok · {proxy.fail_count} failed · priority{' '}
        {proxy.priority}
      </span>
      <label className="flex items-center gap-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
        <input
          type="checkbox"
          checked={proxy.enabled}
          disabled={busy !== null}
          onChange={() => run('toggle')}
          className="size-4 accent-neutral-900 dark:accent-white"
        />
        Enabled
      </label>
      <button
        type="button"
        aria-label="Check proxy"
        disabled={busy !== null}
        onClick={() => run('check')}
        className="grid size-9 place-items-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-400 dark:hover:bg-neutral-800"
      >
        {busy === 'check' ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Activity className="size-4" />
        )}
      </button>
      <button
        type="button"
        aria-label="Delete proxy"
        disabled={busy !== null}
        onClick={() => run('delete')}
        className="grid size-9 place-items-center rounded-full text-red-500 transition-colors hover:bg-red-500/10 disabled:opacity-50"
      >
        {busy === 'delete' ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Trash2 className="size-4" />
        )}
      </button>
    </li>
  )
}
